import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Indicador, PessoaResponsavel, Responsavel } from '@core/models/indicator.model';

@Injectable({ providedIn: 'root' })
export class IndicatorsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/api/indicadores`;
  private readonly responsaveisApiUrl = `${environment.apiUrl}/api/responsaveis/pessoas`;

  readonly indicators = signal<Indicador[]>([]);
  readonly responsaveisPessoas = signal<PessoaResponsavel[]>([]);
  readonly loading = signal(false);
  readonly loadingResponsaveis = signal(false);

  async loadAll(): Promise<void> {
    this.loading.set(true);
    try {
      const [inds, pessoas] = await Promise.all([
        lastValueFrom(this.http.get<Indicador[]>(this.apiUrl)),
        this.getPessoasResponsaveis()
      ]);
      this.indicators.set(inds);
      this.responsaveisPessoas.set(pessoas);
    } finally {
      this.loading.set(false);
    }
  }

  async loadResponsaveisPessoas(): Promise<void> {
    this.loadingResponsaveis.set(true);
    try {
      const data = await this.getPessoasResponsaveis();
      this.responsaveisPessoas.set(data);
    } finally {
      this.loadingResponsaveis.set(false);
    }
  }

  private getPessoasResponsaveis(): Promise<PessoaResponsavel[]> {
    return lastValueFrom(this.http.get<PessoaResponsavel[]>(this.responsaveisApiUrl));
  }

  getById(id: string): Promise<Indicador> {
    return lastValueFrom(this.http.get<Indicador>(`${this.apiUrl}/${id}`));
  }

  async create(indicador: Partial<Indicador>): Promise<Indicador> {
    const res = await lastValueFrom(this.http.post<Indicador>(this.apiUrl, indicador));
    await this.loadAll();
    return res;
  }

  async update(id: string, updates: Partial<Indicador>): Promise<Indicador> {
    const res = await lastValueFrom(this.http.put<Indicador>(`${this.apiUrl}/${id}`, updates));
    await this.loadAll();
    return res;
  }

  async setAtivo(id: string, ativo: boolean): Promise<void> {
    await lastValueFrom(this.http.patch<void>(`${this.apiUrl}/${id}/ativo`, { ativo }));
    await this.loadAll();
  }

  async deletePermanente(id: string): Promise<void> {
    await lastValueFrom(this.http.delete<void>(`${this.apiUrl}/${id}`));
    await this.loadAll();
  }

  getResponsaveis(indicadorId: string): Promise<Responsavel[]> {
    return lastValueFrom(this.http.get<Responsavel[]>(`${this.apiUrl}/${indicadorId}/responsaveis`));
  }

  async assignUser(indicadorId: string, email: string): Promise<void> {
    await lastValueFrom(this.http.post<void>(`${this.apiUrl}/${indicadorId}/responsaveis`, { email }));
    await this.loadAll();
  }

  async removeAssignment(assignmentId: number): Promise<void> {
    await lastValueFrom(this.http.delete<void>(`${environment.apiUrl}/api/responsaveis/${assignmentId}`));
    await this.loadAll();
  }

  async createResponsavel(email: string, indicadorIds: (string | number)[]): Promise<void> {
    const numericIds = indicadorIds.map(id => Number(id));
    await lastValueFrom(this.http.post<void>(this.responsaveisApiUrl, {
      email,
      indicadorIds: numericIds
    }));
    await this.loadAll();
  }

  async addIndicadoresToPessoa(email: string, indicadorIds: (string | number)[]): Promise<void> {
    const numericIds = indicadorIds.map(id => Number(id));
    const encodedEmail = encodeURIComponent(email);
    await lastValueFrom(this.http.post<void>(`${this.responsaveisApiUrl}/${encodedEmail}/indicadores`, {
      indicadorIds: numericIds
    }));
    await this.loadAll();
  }

  async removeIndicadorFromPessoa(email: string, indicadorId: string | number): Promise<void> {
    const encodedEmail = encodeURIComponent(email);
    await lastValueFrom(this.http.delete<void>(`${this.responsaveisApiUrl}/${encodedEmail}/indicadores/${indicadorId}`));
    await this.loadAll();
  }

  async updateResponsavelEmail(emailAtual: string, novoEmail: string): Promise<void> {
    await lastValueFrom(this.http.put<void>(`${environment.apiUrl}/api/responsaveis/pessoas/email`, {
      emailAtual,
      novoEmail
    }));
    await this.loadAll();
  }

  async removePessoa(email: string): Promise<void> {
    const encodedEmail = encodeURIComponent(email);
    await lastValueFrom(this.http.delete<void>(`${this.responsaveisApiUrl}/${encodedEmail}`));
    await this.loadAll();
  }

  async createPendingCollection(indicadorId: string, periodicidade: 'MENSAL' | 'ANUAL', referencia: string): Promise<void> {
    await lastValueFrom(this.http.post<void>(`${environment.apiUrl}/api/coletas/individual`, {
      indicador_id: indicadorId,
      periodicidade,
      mes_ano: periodicidade === 'MENSAL' ? referencia : null,
      ano_referencia: periodicidade === 'ANUAL' ? Number(referencia) : null,
    }));
  }
}
